// frontend/src/components/surveys/VoluntarySurvey.js

/**
 * Voluntary Survey Component
 * Allows users to voluntarily take available surveys
 * Shows available surveys and their completion status
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { 
    MessageSquare, Star, CheckCircle, Clock, 
    ChevronRight, Car, Monitor, AlertCircle 
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import ThemedButton from '../themed/ThemedButton';
import ThemedCard from '../themed/ThemedCard';
import ThemedBadge from '../themed/ThemedBadge';
import surveyService from '../../services/surveyService';
import SurveyDisplay from '../SurveyDisplay';
import { formatDateLocal } from '../../utils/dateTimeFormatter';

const VoluntarySurvey = ({ entityId }) => {
    const { t } = useTranslation();
    const { background, text, border, cn } = useTheme();
    const userId = localStorage.getItem('userId');
    
    // State management
    const [loading, setLoading] = useState(true);
    const [availableSurveys, setAvailableSurveys] = useState([]);
    const [selectedSurvey, setSelectedSurvey] = useState(null);
    const [showSurveyDisplay, setShowSurveyDisplay] = useState(false);
    const [surveyHistory, setSurveyHistory] = useState([]);
    
    // Load available surveys and history
    useEffect(() => {
        loadSurveyData();
    }, [entityId]);
    
    const loadSurveyData = async () => {
        try {
            setLoading(true);

            // Get available surveys for voluntary participation
            const surveys = await surveyService.getAvailableSurveys(entityId);

            // surveyService returns an array directly
            setAvailableSurveys(surveys || []);

            // Load survey history separately if needed
            // Note: Currently no history endpoint, surveys are always available
            setSurveyHistory([]);
        } catch (error) {
            console.error('Error loading surveys:', error);
            toast.error(t('errorLoadingSurveys'));
        } finally {
            setLoading(false);
        }
    };
    
    // Handle survey selection
    const handleStartSurvey = async (survey) => {
        try {
            // Load survey for viewing (in user's preferred language)
            const result = await surveyService.getSurveyForViewing(
                entityId,
                survey.survey_type
            );

            // Extract survey from result
            const config = result?.survey || result;

            if (config && config.questions && config.questions.length > 0) {
                let previousResponses = {};
                
                // For system survey, load previous responses if they exist
                if (survey.survey_type === 'system') {
                    try {
                        const responseData = await surveyService.getUserResponses(entityId, 'system');
                        if (responseData.hasResponse) {
                            previousResponses = responseData.responses;
                            toast.info(t('loadingPreviousResponses'));
                        }
                    } catch (error) {
                        console.error('Error loading previous responses:', error);
                    }
                }
                
                setSelectedSurvey({
                    ...survey,
                    questions: config.questions,
                    survey_id: config.survey_id,
                    previousResponses: previousResponses,
                    isModification: survey.survey_type === 'system' && Object.keys(previousResponses).length > 0
                });
                setShowSurveyDisplay(true);
            } else {
                toast.warning(t('surveyNotConfigured'));
            }
        } catch (error) {
            console.error('Error loading survey configuration:', error);
            toast.error(t('errorLoadingSurveyQuestions'));
        }
    };
    
    // Handle survey completion
    const handleSurveyComplete = async () => {
        setShowSurveyDisplay(false);
        setSelectedSurvey(null);
        
        // Reload data to update completion status
        await loadSurveyData();
        
        toast.success(t('thankYouForFeedback'));
    };
    
    // Get icon for survey type
    const getSurveyIcon = (type) => {
        switch(type) {
            case 'service':
                return Car;
            case 'system':
                return Monitor;
            default:
                return MessageSquare;
        }
    };
    
    // Check if survey can be taken
    const canTakeSurvey = (surveyType) => {
        if (surveyType === 'service') {
            // Service survey can be taken per trip - always available
            return true;
        } else if (surveyType === 'system') {
            // System survey - check if already completed (can modify)
            const completed = surveyHistory.find(h => 
                h.survey_type === 'system' && h.completed_at
            );
            return true; // Always allow, but show different button text
        }
        return false;
    };
    
    // Get button text based on survey type and history
    const getSurveyButtonText = (surveyType) => {
        if (surveyType === 'system') {
            const completed = surveyHistory.find(h => 
                h.survey_type === 'system' && h.completed_at
            );
            return completed ? t('modifySurvey') : t('takeSurvey');
        }
        return t('takeSurvey');
    };
    
    if (loading) {
        return (
            <div className={cn("flex justify-center items-center h-64", text.secondary)}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3">{t('loadingSurveys')}...</span>
            </div>
        );
    }
    
    return (
        <>
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h1 className={cn("text-2xl font-bold", text.primary)}>
                        {t('feedbackSurveys')}
                    </h1>
                    <p className={cn("text-sm mt-1", text.secondary)}>
                        {t('helpUsImproveByProvidingFeedback')}
                    </p>
                </div>
                
                {/* Available Surveys */}
                {availableSurveys.length === 0 ? (
                    <ThemedCard className="p-8 text-center">
                        <AlertCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p className={cn("text-lg", text.secondary)}>
                            {t('noSurveysAvailable')}
                        </p>
                        <p className={cn("text-sm mt-2", text.secondary)}>
                            {t('checkBackLater')}
                        </p>
                    </ThemedCard>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                        {availableSurveys.map((survey) => {
                            const Icon = getSurveyIcon(survey.survey_type);
                            const canTake = canTakeSurvey(survey.survey_type);
                            const buttonText = getSurveyButtonText(survey.survey_type);
                            const isSystemCompleted = survey.survey_type === 'system' && 
                                surveyHistory.some(h => h.survey_type === 'system' && h.completed_at);
                            
                            return (
                                <ThemedCard 
                                    key={survey.survey_type}
                                    className={cn(
                                        "p-6 transition-all hover:shadow-lg",
                                        isSystemCompleted && "opacity-75"
                                    )}
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={cn(
                                                "p-3 rounded-lg",
                                                survey.survey_type === 'service' 
                                                    ? "bg-blue-100 dark:bg-blue-900/30" 
                                                    : "bg-purple-100 dark:bg-purple-900/30"
                                            )}>
                                                <Icon className={cn(
                                                    "w-6 h-6",
                                                    survey.survey_type === 'service'
                                                        ? "text-blue-600 dark:text-blue-400"
                                                        : "text-purple-600 dark:text-purple-400"
                                                )} />
                                            </div>
                                            <div>
                                                <h3 className={cn("font-semibold text-lg", text.primary)}>
                                                    {survey.title || (
                                                        survey.survey_type === 'service' 
                                                            ? t('serviceQualitySurvey')
                                                            : t('systemFeedbackSurvey')
                                                    )}
                                                </h3>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Clock className="w-4 h-4 text-gray-400" />
                                                    <span className={cn("text-sm", text.secondary)}>
                                                        {survey.estimated_time || '2-3'} {t('minutes')}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {isSystemCompleted && (
                                            <ThemedBadge variant="success">
                                                <CheckCircle className="w-3 h-3 mr-1" />
                                                {t('completed')}
                                            </ThemedBadge>
                                        )}
                                    </div>
                                    
                                    <p className={cn("text-sm mb-4", text.secondary)}>
                                        {(
                                            survey.survey_type === 'service'
                                                ? t('shareYourExperienceWithOurDriversAndVehicles')
                                                : t('helpUsImproveTheApplicationExperience')
                                        )}
                                    </p>
                                    
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Star className="w-4 h-4 text-yellow-500" />
                                            <span className={cn("text-sm", text.secondary)}>
                                                {survey.question_count || 5} {t('questions')}
                                            </span>
                                        </div>
                                        
                                        <ThemedButton
                                            onClick={() => handleStartSurvey(survey)}
                                            disabled={!canTake}
                                            variant="primary"
                                            size="sm"
                                        >
                                            {buttonText}
                                            <ChevronRight className="w-4 h-4 ml-1" />
                                        </ThemedButton>
                                    </div>
                                    
                                    {survey.survey_type === 'system' && isSystemCompleted && (
                                        <p className={cn("text-xs mt-3", text.secondary)}>
                                            {t('lastModified')}: {formatDateLocal(
                                                surveyHistory.find(h => h.survey_type === 'system')?.completed_at
                                            )}
                                        </p>
                                    )}
                                </ThemedCard>
                            );
                        })}
                    </div>
                )}
                
                {/* Survey History Summary */}
                {surveyHistory.length > 0 && (
                    <ThemedCard className="p-4">
                        <h3 className={cn("font-medium mb-3", text.primary)}>
                            {t('yourFeedbackHistory')}
                        </h3>
                        <div className="space-y-2">
                            {surveyHistory.slice(0, 5).map((history, index) => (
                                <div 
                                    key={index}
                                    className="flex items-center justify-between py-2 border-b last:border-0"
                                >
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                        <span className={cn("text-sm", text.primary)}>
                                            {history.survey_type === 'service' 
                                                ? t('serviceQualitySurvey')
                                                : t('systemFeedbackSurvey')}
                                        </span>
                                    </div>
                                    <span className={cn("text-sm", text.secondary)}>
                                        {formatDateLocal(history.completed_at)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </ThemedCard>
                )}
            </div>
            
            {/* Survey Display Modal */}
            {showSurveyDisplay && selectedSurvey && (
                <SurveyDisplay
                    survey={selectedSurvey}
                    entityId={entityId}
                    isVoluntary={true}
                    displayDelay={0} // No delay for voluntary surveys
                    onClose={() => {
                        setShowSurveyDisplay(false);
                        setSelectedSurvey(null);
                    }}
                    onComplete={handleSurveyComplete}
                />
            )}
        </>
    );
};

export default VoluntarySurvey;
